import { ChangeEvent, Dispatch, SetStateAction } from "react"
import { ReadAcrossDAGRequestModel } from "../../../../api/models"
import InputField from "../../../../components/Form/InputField";
import SelectField from "../../../../components/Form/SelectFields";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import NumberSlider from "../../../../components/Data/NumberSlider";
import TagsField from "../../../../components/Form/TagsField";

const fingerprintOptions = [
    { value: 'ecfp6_1024', label: 'ECFP' },
    { value: 'maccs', label: 'MACCS' },
]

const weightingOptions = [
    { value: 'uniform', label: 'Uniform' },
    { value: 'distance', label: 'Distance' },
    { value: 'tanimoto', label: 'Tanimoto' },
]

// const modelTypeOptions = [
//     { value: 'classification', label: 'Classification' },
//     { value: 'regression', label: 'Regression' }
// ];

const internal_dsType_label = process.env.REACT_APP_META_TITLE || 'Assay Central';

const dsTypeOptions = [
    { value: 'toxref', label: 'ToxRef' },
    { value: 'internal', label: internal_dsType_label }
]

const dsSelectionType = [
    { value: 'datasets', label: 'Datasets' },
    { value: 'model_families', label: 'Model Families' }
]

export default function SetRAParameters({
    form, 
    setForm, 
    numNeighors, 
    setNumNeighbors,
    showSelect,
    selectedDsType,
    updateSelectedDSType,
    updateRAType,
}: {
    form: ReadAcrossDAGRequestModel,
    setForm: Dispatch<SetStateAction<ReadAcrossDAGRequestModel>>,
    showSelect: boolean,
    numNeighors: number,
    setNumNeighbors: Dispatch<SetStateAction<number>>,
    selectedDsType: { value: string, label: string },
    updateSelectedDSType: (option: { value: string, label: string }) => void,
    updateRAType: (option: { value: string, label: string }) => void,
}) {

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        })
    };

    const handleSelect = (e: {value:string | number, label: string | number} | {value:string | number, label: string | number}[], name: string) => {
        setForm({
            ...form,
            [name]: Array.isArray(e) ? e.map(val => val.value) : e.value,
            dataset: name === 'readacross_type' && !Array.isArray(e) && e.value === 'internal' ? {
                dataset_hash: ''
            } : undefined,
        })
    }

    const handleAddTag = (category: string, tag: string) => {
        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setForm({
            ...form,
            tags: form!.tags!.concat(addedTag)

        })
    }

    const handleRemoveTag = (index: number) => {
        setForm({
            ...form,
            tags: form!.tags!.filter((t, i) => i !== index)
        })
    }


    return (
        <>
            <InputField 
                label="Result Name"
                type='text'
                id='name'
                name='name'
                value={form.name as string}
                isRequired
                handleChange={handleInput}
            />
            <div className="w-full flex justify-between items-end gap-4">
                <ConditionalSelectField label="Fingerprint" showSelect={showSelect}> 
                    <SelectField 
                        label="Fingerprint"
                        id="fp_type"
                        name='fp_type'
                        value={fingerprintOptions.find(opt => opt.value === form.fp_type!)!}
                        options={fingerprintOptions}
                        isRequired
                        isCreatable={false}
                        isMulti={false}
                        handleChange={handleSelect}
                        menuPortalTarget={document.getElementById('generate-read-across-popup')}
                    />
                </ConditionalSelectField>
                <ConditionalSelectField label="Weighting" showSelect={showSelect}> 
                    <SelectField 
                        label="Weighting"
                        id="weighting"
                        name='weighting'
                        value={weightingOptions.find(opt => opt.value === form.weighting!)!}
                        options={weightingOptions}
                        isRequired
                        isCreatable={false}
                        isMulti={false}
                        handleChange={handleSelect}
                        menuPortalTarget={document.getElementById('generate-read-across-popup')}
                    />
                </ConditionalSelectField>
            </div>
            <div className="w-full">
                <NumberSlider 
                    value={numNeighors}
                    setValue={setNumNeighbors}
                    label="Number of Nearest Neighbors"
                    range={{
                        step: 1,
                        min: 1,
                        max: 15
                    }}
                />
            </div>
            <div className="w-full flex justify-between items-end gap-4">
                {/* Not used anymore
                **/}
                {/* <ConditionalSelectField label="Model Type" showSelect={showSelect}>
                    <SelectField 
                        label="Model Type"
                        id="model_type"
                        name='model_type'
                        value={modelTypeOptions.find(opt => opt.value === form.model_type!)!}
                        options={modelTypeOptions}
                        isRequired
                        isCreatable={false}
                        isMulti={false}
                        handleChange={handleSelect}
                        menuPortalTarget={document.getElementById('generate-read-across-popup')}
                    />
                </ConditionalSelectField> */}
                <ConditionalSelectField label="Dataset Type" showSelect={showSelect}>
                    <SelectField 
                        label="Dataset Type"
                        id="readacross_type"
                        name='readacross_type'
                        value={dsTypeOptions.find(opt => opt.value === form.readacross_type!)!}
                        options={dsTypeOptions}
                        isRequired
                        isCreatable={false}
                        isMulti={false}
                        handleChange={(e) => {
                            const option = e as {value:string, label: string}
                            updateRAType(option);
                        }}
                        menuPortalTarget={document.getElementById('generate-read-across-popup')}
                    />
                </ConditionalSelectField>
            </div>
            
            {form.readacross_type! === 'internal' && (
                <div className="w-full flex justify-between items-end gap-4">
                    <ConditionalSelectField label="Select from" showSelect={showSelect}>
                        <SelectField 
                            label="Select from"
                            id="dsSelectionType"
                            name='dsSelectionType'
                            value={selectedDsType}
                            options={dsSelectionType}
                            isRequired
                            isCreatable={false}
                            isMulti={false}
                            handleChange={(e) => {
                                const option = e as {value:string, label: string}
                                updateSelectedDSType(option);
                            }}
                            menuPortalTarget={document.getElementById('generate-read-across-popup')}
                        />
                    </ConditionalSelectField>
                </div>
            )}

            <TagsField 
                label="Read-Across"
                tags={form.tags! as string[]}
                showSelect={showSelect}
                menuPortalTarget={document.getElementById('generate-read-across-popup')}
                handleAddTag={handleAddTag}
                handleRemoveTag={handleRemoveTag}
            />
        </>
    )
}